import { useEffect, useState } from 'react';
import { idCardService } from '../../services/idCardService';
import type { IdCardAudience } from '../../types/idCards';
import { fetchPhotoDataUrls } from '../../utils/pdf';
import { personFromEnrollment, personFromStaff, samplePerson, type CardPerson } from './cardPeople';

/**
 * Someone real to preview a design with — the first enrolled student or
 * employee — so staff judge the card with their own school's data. Falls
 * back to sample details when there is nobody yet, or when this user may
 * design cards but not see that list (a deliberate recovery: the preview
 * still works, it just shows a sample).
 */
export const usePreviewPerson = (audience: IdCardAudience, sessionName?: string) => {
  const [person, setPerson] = useState<CardPerson>(() => samplePerson(audience));
  const [photo, setPhoto] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPerson(samplePerson(audience));
    setPhoto(null);
    (async () => {
      try {
        const found = audience === 'student'
          ? (await idCardService.listStudents({ limit: 1 })).rows.map(r => personFromEnrollment(r, sessionName))[0]
          : (await idCardService.listStaff({ limit: 1 })).users.map(personFromStaff)[0];
        if (!found || cancelled) return;
        const photos = await fetchPhotoDataUrls([found.photoFilename]);
        if (cancelled) return;
        setPerson(found);
        setPhoto(photos[found.photoFilename ?? ''] ?? null);
      } catch {
        // Keep the sample — see above.
      }
    })();
    return () => { cancelled = true; };
  }, [audience, sessionName]);

  return { person, photo };
};
